import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Car, MoreVerticalIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import OrderEdit from "../Edit";
import AssignDriver from "@/components/common/orders/AssignDriver";
import { taxiStatusColor, taxiStatusItems } from "@/constants/status";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { Driver, Vehicle } from "@/interface/entityInterface";
import OrderRoute from "../components/OrderRoute";
import OrderTrackLink from "../components/OrderTrackLink";
import AssignVehicle from "@/core-modules/devices/components/AssignVehicle";

interface UseOrderColumnsProps {
  configs: { vehicle_assign: boolean, driver_assign: boolean }
  vehicles: Vehicle[];
  drivers: Driver[];
}

export const useOrderColumns = ({
  configs,
  vehicles,
  drivers,
}: UseOrderColumnsProps): ColumnDef<any>[] => {
  const driverAssign =
    configs.driver_assign
      ? {
        accessorKey: "assign_drivers",
        header: ({ column }: any) => (
          <DataTableColumnHeader column={column} title="Assigned Driver" />
        ),
        enableSorting: false,
        cell: ({ row }: any) => {
          const status = row.original.status;
          // if status is COMPLETED or _IN_PROGRESS, dont show the AssignDriver component
          if (status === "COMPLETED" || status === "IN_PROGRESS") {
            const driver = row.original.driver;
            return <span>{driver ? driver.name : "Unassigned"}</span>;
          }
          return <AssignDriver order={row.original} drivers={drivers} />;
        },
      }
      : null;

  const orderCols = [
    {
      accessorKey: "id",
      header: ({ column }: any) => (
        <DataTableColumnHeader column={column} title="ID" />
      ),
      enableSorting: false,
      cell: ({ row }: any) => (
        <div className="capitalize">{row.getValue("id")}</div>
      ),
    },
    {
      accessorKey: "created_at",
      header: ({ column }: any) => (
        <DataTableColumnHeader column={column} title="Created at" />
      ),
      cell: ({ row }: any) => {
        const value: any = row.getValue("created_at");

        if (!value) return <span className="text-gray-400">No time</span>;

        try {
          const formatted = format(new Date(value), "LLL dd, y, HH:mm");
          return <span>{formatted}</span>;
        } catch (error) {
          return <span className="text-red-500">Invalid date</span>;
        }
      },
    },
    {
      accessorKey: "source",
      header: ({ column }: any) => (
        <DataTableColumnHeader column={column} title="Source" />
      ),
      enableSorting: false,
      cell: ({ row }: any) => row.original.commission_partner_id && row.original.commission_partner_id > 0 ? (
        <div className="flex flex-col text-sm space-y-1 max-w-xs">
          <Badge variant="outline" className="text-gray-500">
            PARTNER
          </Badge>
          <span className="font-medium">{row.original.commission_partner.name}</span>
        </div>
      ) : (
        <Badge variant="outline" className="text-gray-500">
          {row.getValue("source")}
        </Badge>
      ),
    },
    {
      accessorKey: "client_name",
      header: ({ column }: any) => (
        <DataTableColumnHeader column={column} title="Client Details" />
      ),
      cell: ({ row }: any) => {
        const clientName = row.original.client_name;
        const clientNameText = clientName ? clientName : "No name";
        const clientPhone = row.original.client_phone;
        const clientPhoneText = clientPhone ? clientPhone : "No phone";

        return (
          <div
            className="flex flex-col text-sm space-y-1 max-w-xs"
            title={clientNameText + ", " + clientPhoneText}
          >
            {clientName ? (
              <span className="font-medium">{clientNameText}</span>
            ) : (
              <span className="text-gray-400">No name</span>
            )}

            {clientPhone ? (
              <span className="font-medium">{clientPhoneText}</span>
            ) : (
              <span className="text-gray-400">No phone</span>
            )}
          </div>
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: "pickup",
      header: ({ column }: any) => (
        <DataTableColumnHeader column={column} title="Pickup & Dropoff" />
      ),
      enableSorting: false,
      cell: ({ row }: any) => {
        const pickup: string = row.original.pickup;
        const dropoff: string = row.original.dropoff;
        return (
          <div
            className="flex flex-col text-sm space-y-1 max-w-xs"
            title={pickup + " --> " + (dropoff ? dropoff : "No address")}
          >
            <span className="font-medium">{pickup}</span>

            {dropoff ? (
              <span className="font-medium">{dropoff}</span>
            ) : (
              <span className="text-gray-400">No address</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "vehicle",
      header: ({ column }: any) => (
        <DataTableColumnHeader column={column} title="Vehicle" />
      ),
      cell: ({ row }: any) => {
        const vehicle = row.original.driver?.vehicle;
        const vehicleName = vehicle?.nickname || vehicle?.license_plate;

        return !configs.driver_assign &&
          row.original.status !== "COMPLETED" &&
          row.original.status !== "IN_PROGRESS" ? (
          <AssignVehicle
            url="taxi-orders"
            id={row.original.id}
            vehicleId={row.original?.vehicle_id}
            vehicleList={vehicles}
          />
        ) : (
          <div className="flex items-center space-x-2 text-sm ml-2">
            <Car className="w-4 h-4 text-gray-600" />
            {vehicle ? (
              <span className="font-medium">{vehicleName}</span>
            ) : (
              <span className="text-gray-400">No assigned vehicle</span>
            )}
          </div>
        );
      },
      enableSorting: false,
      sortingFn: (rowA, rowB) => {
        const vehicleA = rowA.original.driver?.vehicle;
        const vehicleB = rowB.original.driver?.vehicle;

        const nameA = vehicleA?.nickname || vehicleA?.license_plate || "";
        const nameB = vehicleB?.nickname || vehicleB?.license_plate || "";
        return nameA.localeCompare(nameB);
      },
    },
    {
      accessorKey: "driver_arrived",
      header: ({ column }: any) => (
        <DataTableColumnHeader column={column} title="Driver Arrived" />
      ),
      cell: ({ row }: any) => {
        // driver arrived is a boolean
        const arrived = row.original.driver_arrived;
        return arrived ? (
          <Badge className="bg-green-100 text-green-800">Yes</Badge>
        ) : (
          <Badge className="bg-red-100 text-red-800">No</Badge>
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: "pickup_time",
      header: ({ column }: any) => (
        <DataTableColumnHeader column={column} title="Pickup Time" />
      ),
      cell: ({ row }: any) => {
        const value: any = row.getValue("pickup_time");

        if (!value) return <span className="text-gray-400">No time</span>;

        try {
          const formatted = format(new Date(value), "LLL dd, y, HH:mm");
          return <span>{formatted}</span>;
        } catch (error) {
          return <span className="text-red-500">Invalid date</span>;
        }
      },
    },
    {
      accessorKey: "status",
      header: ({ column }: any) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }: any) => {
        const value = row.getValue("status") as string;
        return (
          <Badge className={taxiStatusColor[value].badge}>
            {taxiStatusItems.find((item) => item.status === value)?.label ||
              value}
          </Badge>
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: "fare",
      header: ({ column }: any) => (
        <DataTableColumnHeader column={column} title="Fare" />
      ),
      cell: ({ row }: any) => {
        return (
          <div className="flex items-center space-x-2 text-sm">
            {row.original.fare ? (
              <span className="font-medium">
                {row.original.fare.toFixed(2)}{" "}
                <span className="font-medium">{row.original.currency}</span>
              </span>
            ) : (
              <span className="text-gray-400">No fare</span>
            )}
          </div>
        );
      },
    },
    driverAssign,
    {
      id: "actions",
      cell: ({ row }: any) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
              size="icon"
            >
              <MoreVerticalIcon />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-32">
            <OrderEdit id={row.original.id} />

            {row.original.status === "COMPLETED" && (
              <OrderRoute record={row.original} />
            )}

            {row.original.status != "COMPLETED" && (
              <OrderTrackLink
                id={row.original.id}
                settings_id={row.original.settings_id}
                lat={row.original.pickup_latitude}
                lng={row.original.pickup_longitude}
              />
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ].filter(Boolean);

  return orderCols;
};
